/**
 * WHICH SUPABASE PROJECT THIS BUILD TALKS TO.
 *
 * The sibling of src/firebase.ts, and the same two questions kept apart: this
 * file answers WHICH COPY, and src/backend/index.ts answers WHICH KIND of
 * database. Neither knows about the other.
 *
 * THE KEY BELOW IS PUBLIC BY DESIGN, exactly as Firebase's is. It identifies
 * the project and grants nothing on its own — every row it can reach is
 * decided by the policies in supabase/migrations, which is why those were
 * written and proved before anything pointed here. A key that granted access
 * would make this file a secret and the policies decoration.
 *
 * TWO PROJECTS, CHOSEN BY THE BUILD'S OWN STAMP — the way firebase.ts already
 * does it. This stopped being a detail on 13 August:
 *
 * REAL TESTERS CANNOT SHARE A DATABASE WITH THE TEST SUITE. `npm test` creates
 * workspaces, people and issues in the project below and deletes them again,
 * dozens of times a day, and the teardowns are `delete` statements scoped by a
 * fixture id. One wrong `where` clause is somebody's real board. The suite is
 * also not the only risk — this project gets hand-edited during development,
 * which is correct for a test copy and unthinkable for one a team depends on.
 *
 * So a second project exists for people doing real work, and which one a build
 * talks to is decided HERE, once, from `__ENV__`. Nothing else in the app knows
 * there is a choice.
 *
 * scripts/test-nothing-tests-against-production.mjs fails the build if anything
 * that writes or tears down names the production project.
 */
import { createClient } from '@supabase/supabase-js';
import { companyInAddress } from './company';

/** The copy that gets broken on purpose. Everything automated points here. */
const UAT = {
  url: 'https://taynlgjcarlltnqojhnq.supabase.co',
  key: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRheW5sZ2pjYXJsbHRucW9qaG5xIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODYwNTkxNjIsImV4cCI6MjEwMTYzNTE2Mn0.mkwYHNfUZoLjmY-rYVPvIL3SwgqypwYhMpOdVZ8YIPQ',
};

/**
 * The copy real people work in. EMPTY UNTIL THE PROJECT EXISTS, and empty is
 * the safe state: a production build without these values refuses to start
 * rather than quietly falling back to the test copy. Falling back is the
 * failure that matters — testers would appear to be working while sitting in
 * the database the suite wipes.
 *
 * Filled in by scripts/stand-up-production.mjs once the project is made.
 */
const PRODUCTION = {
  url: 'https://kxqjozpfjccygjjewxtk.supabase.co',
  key: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt4cWpvenBmamNjeWdqamV3eHRrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODY1OTg0NDIsImV4cCI6MjEwMjE3NDQ0Mn0.0bZLVqs4Uwojb-r31nUNXC-08MUPoCJ4-yZsYRnPJ_g',
};

/*
 * `__ENV__` IS STAMPED BY VITE AND DOES NOT EXIST ANYWHERE ELSE. The test
 * suite imports this file straight into node, where reading it throws — which
 * is how eight suites went red the moment this became a choice.
 *
 * Absent means the test copy, and that is the safe direction rather than a
 * convenience: anything running outside a real build is automation, and
 * automation must never find its way to the project real people work in.
 */
const stamped = typeof __ENV__ === 'undefined' ? 'uat' : __ENV__;

/*
 * THE GATE'S OWN BACKEND. The test gate can run against a dedicated, throwaway
 * Supabase project so it never loads the shared test copy real people use. When
 * it does, it sets GATE_BACKEND_URL (+ GATE_BACKEND_ANON). This override applies
 * ONLY in node — reached through `globalThis.process`, which a browser does not
 * have — and ONLY when `__ENV__` is absent, so a real build (always stamped)
 * can never be redirected by it. A customer's browser is untouched.
 */
const gateEnv = (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } })
  .process?.env;
const gate =
  typeof __ENV__ === 'undefined' && gateEnv?.GATE_BACKEND_URL
    ? { url: gateEnv.GATE_BACKEND_URL, key: gateEnv.GATE_BACKEND_ANON ?? '' }
    : null;

const chosen = gate ?? (stamped === 'live' ? PRODUCTION : UAT);

if (stamped === 'live' && !chosen.url) {
  throw new Error(
    'This is a production build and no production Supabase project is set. ' +
      'Run scripts/stand-up-production.mjs. Refusing to fall back to the test copy.'
  );
}

export const SUPABASE_URL = chosen.url;
export const SUPABASE_ANON_KEY = chosen.key;

/*
 * THE COMPANY THE ADDRESS NAMES, ON EVERY REQUEST — #218.
 *
 * SET ONCE, AT THE TOP, AND NEVER CHANGED. It is read from the path, and the
 * path cannot change without a reload — moving company reloads on purpose (see
 * WorkspaceSwitcher), which is what makes a value fixed at creation correct
 * rather than merely convenient.
 *
 * WHY A HEADER AND NOT THE TOKEN. Writing the company onto the account on each
 * page load would behave identically until somebody opens two tabs: the second
 * tab's write drags the first across, silently, which is the very fault #218 is
 * about wearing a different face. A header belongs to one request, so two tabs
 * cannot fight. test-the-address-says-the-company.ts holds that case.
 *
 * IT IS NOT A CREDENTIAL. `my_tenant()` honours it only where a real membership
 * exists, so this names a company rather than granting anything — which is why
 * it is safe for it to be sitting in plain sight in the address bar.
 */
const company = companyInAddress();

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  ...(company ? { global: { headers: { 'x-flightdeck-company': company } } } : {}),
  auth: {
    // The sign-in link lands back on the app with its proof in the URL; this
    // is what reads it and puts the session away. Without it somebody clicks
    // the link, arrives, and is still signed out.
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
    flowType: 'pkce',
  },
});
